import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ref, get, set, serverTimestamp } from '../firebase';
import { database } from '../firebase';
import { FiEdit, FiX, FiImage, FiVideo, FiSave } from 'react-icons/fi';

const EditMedia = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const mediaType = location.state?.mediaType; // Get mediaType from Home.jsx
  const [mediaItem, setMediaItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMediaItem = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('currentUser'));
        if (!userData || !userData.mobile) {
          setError('Please log in to edit media.');
          navigate('/login');
          return;
        }
        const sanitizedMobile = userData.mobile.replace(/[^0-9]/g, '_');

        let mediaRefPath;
        let fetchedMediaType = mediaType;

        // If mediaType is not provided, try fetching to determine type
        if (!fetchedMediaType) {
          // Try fetching as a photo first
          mediaRefPath = ref(database, `videoplayer/photoUpload/${sanitizedMobile}/${id}`);
          let snapshot = await get(mediaRefPath);
          if (snapshot.exists()) {
            fetchedMediaType = 'photo';
            setMediaItem({ ...snapshot.val(), type: fetchedMediaType, id, channelMobile: sanitizedMobile });
            setFormData({
              title: snapshot.val().title || '',
              description: snapshot.val().description || '',
            });
            setLoading(false);
            return;
          }

          // If not a photo, try fetching as a video
          mediaRefPath = ref(database, `videoplayer/videoUpload/${sanitizedMobile}/${id}`);
          snapshot = await get(mediaRefPath);
          if (snapshot.exists()) {
            fetchedMediaType = 'video';
            setMediaItem({ ...snapshot.val(), type: fetchedMediaType, id, channelMobile: sanitizedMobile });
            setFormData({
              title: snapshot.val().title || '',
              description: snapshot.val().description || '',
            });
            setLoading(false);
            return;
          }
        } else {
          // Use provided mediaType
          mediaRefPath = ref(database, `videoplayer/${fetchedMediaType}Upload/${sanitizedMobile}/${id}`);
          const snapshot = await get(mediaRefPath);
          if (snapshot.exists()) {
            setMediaItem({ ...snapshot.val(), type: fetchedMediaType, id, channelMobile: sanitizedMobile });
            setFormData({
              title: snapshot.val().title || '',
              description: snapshot.val().description || '',
            });
            setLoading(false);
            return;
          }
        }

        setError('Media item not found.');
        setLoading(false);
      } catch (err) {
        console.error('Error fetching media item:', err);
        setError('Failed to load media item. Please try again.');
        setLoading(false);
      }
    };

    fetchMediaItem();
  }, [id, navigate, mediaType]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      setError('Title is required.');
      return;
    }

    try {
      const sanitizedMobile = mediaItem.channelMobile;
      const mediaRefPath = ref(database, `videoplayer/${mediaItem.type}Upload/${sanitizedMobile}/${mediaItem.id}`);
      await set(mediaRefPath, {
        ...mediaItem,
        title: formData.title,
        description: mediaItem.type === 'video' ? formData.description : mediaItem.description, // Only update description for videos
        uploadDate: mediaItem.uploadDate || serverTimestamp(),
      });
      alert('Media updated successfully!');
      navigate('/home');
    } catch (err) {
      console.error('Error updating media:', err);
      setError('Failed to update media. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate('/home');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex items-center justify-center p-4">
        <div className="text-indigo-600 text-lg font-semibold animate-pulse">Loading media...</div>
      </div>
    );
  }

  if (error || !mediaItem) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex items-center justify-center p-4">
        <div className="text-red-600 text-lg font-semibold">{error || 'Media item not found.'}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex items-center justify-center p-4 sm:p-6">
      <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 max-w-lg w-full animate-slide-in">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 flex items-center justify-center gap-2">
          <FiEdit className="text-indigo-600" />
          Edit {mediaItem.type === 'photo' ? 'Photo' : 'Video'}
        </h2>
        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm border border-red-200 animate-pulse">
            {error}
          </div>
        )}
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              {mediaItem.type === 'photo' ? <FiImage className="text-indigo-600" /> : <FiVideo className="text-indigo-600" />}
              Media Preview
            </label>
            {mediaItem.type === 'photo' ? (
              <img
                src={mediaItem.photoURL}
                alt={mediaItem.title}
                className="w-full h-auto max-h-64 object-contain rounded-lg shadow-md"
              />
            ) : (
              <video
                src={mediaItem.videoURL}
                poster={mediaItem.thumbnailURL}
                controls
                className="w-full h-auto max-h-64 rounded-lg shadow-md"
              />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter title"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-all duration-300 hover:border-indigo-300"
              required
            />
          </div>
          {mediaItem.type === 'video' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter description"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-all duration-300 hover:border-indigo-300"
                rows="4"
              />
            </div>
          )}
          <div className="flex gap-4">
            <button
              onClick={handleCancel}
              className="w-1/2 bg-gray-600 text-white py-2.5 rounded-lg hover:bg-gray-700 focus:ring-2 focus:ring-gray-500 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <FiX />
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="w-1/2 bg-indigo-600 text-white py-2.5 rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <FiSave />
              Update
            </button>
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default EditMedia;